import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Calendar, Hand, Trophy, Users2, Star, Shield, Flag, Phone, Mail, ShieldCheck, Facebook, PenLine, Lock } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { fileUrl } from "@/components/ImageUpload";
import RatingHierarchy from "@/components/ratings/RatingHierarchy";
import AssessmentBreakdown from "@/components/ratings/AssessmentBreakdown";
import CommunityRequestCard from "@/components/ratings/CommunityRequestCard";
import ShareRatingCard from "@/components/ratings/ShareRatingCard";
import CasualSessionsCard from "@/components/ratings/CasualSessionsCard";
import TrendChart from "@/components/ratings/TrendChart";
import { EligibilityList } from "@/components/ratings/RatingForm";
import { localPlayerHistory } from "@/lib/openPlayStore";
import { useAuth } from "@/context/AuthContext";
import { CAT } from "@/lib/ratings";
import api, { formatApiError } from "@/lib/api";

const HANDS = { right: "Right-handed", left: "Left-handed", ambidextrous: "Ambidextrous", ambi: "Ambidextrous" };
const PREFS = { singles: "Singles", doubles: "Doubles", mixed: "Mixed Doubles", recreational: "Recreational", competitive: "Competitive" };
const TABS = [["overview", "Overview"], ["ratings", "Ratings"], ["matches", "Match History"], ["events", "Events"], ["feedback", "Community Feedback"], ["stats", "Statistics"]];

const Card = ({ title, icon: Icon, children, testid, right }) => (
  <div className="rounded-2xl glow-border overflow-hidden" style={{ background: "var(--surface)" }} data-testid={testid}>
    {title && <div className="px-5 py-3.5 border-b flex items-center gap-2 font-display font-bold" style={{ borderColor: "var(--border)" }}>{Icon && <Icon className="w-4 h-4 text-primary-c" />}{title}<span className="ml-auto">{right}</span></div>}
    <div className="p-5">{children}</div>
  </div>
);
const Empty = ({ children }) => <div className="py-6 text-center text-sm" style={{ color: "var(--text-2)" }}>{children}</div>;
const Info = ({ icon: Icon, label, value }) => (
  <div><div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><Icon className="w-3 h-3" />{label}</div><div className="text-sm mt-0.5" style={{ color: value ? "var(--text)" : "var(--text-2)" }}>{value || "—"}</div></div>
);
const WL = ({ win }) => <span className="text-[10px] mono font-bold rounded px-1.5 py-0.5 shrink-0" style={win ? { background: "rgba(34,197,94,0.16)", color: "#22c55e" } : { background: "rgba(239,68,68,0.16)", color: "#ef4444" }}>{win ? "W" : "L"}</span>;
const Stat = ({ label, value }) => <div className="rounded-xl glass px-3 py-2.5 text-center"><div className="font-display font-black text-xl text-primary-c tabular-nums">{value}</div><div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{label}</div></div>;

function Header({ p, isSelf }) {
  const nav = useNavigate();
  const photo = p.photo ? fileUrl(p.photo) : p.picture;
  return (
    <div className="rounded-3xl glow-border p-6 sm:p-8" style={{ background: "var(--surface)" }} data-testid="player-profile-view">
      <div className="flex items-center gap-4 sm:gap-5">
        {photo ? <img src={photo} alt={p.name} className="w-16 h-16 sm:w-24 sm:h-24 shrink-0 rounded-2xl object-cover glow-border" />
          : <div className="w-16 h-16 sm:w-24 sm:h-24 shrink-0 rounded-2xl glass flex items-center justify-center font-display font-black text-3xl text-primary-c">{(p.name || "?")[0]}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <h1 className="font-display font-black text-2xl sm:text-3xl min-w-0 break-words" data-testid="ppv-name">{p.name} <span className="inline-block align-middle"><VerifiedBadge verified={p.verified} size={22} /></span></h1>
            {isSelf && <button data-testid="edit-profile-btn" aria-label="Edit profile" title="Edit profile" onClick={() => nav("/profile?return=/players/me")} className="btn-glow rounded-full shrink-0 w-9 h-9 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 sm:ml-auto text-sm font-bold flex items-center justify-center gap-2"><PenLine className="w-4 h-4" /><span className="hidden sm:inline">Edit Profile</span></button>}
          </div>
          <div className="text-xs mono mt-1" style={{ color: "var(--text-2)" }} data-testid="ppv-player-code">Player ID {p.player_code}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        <Info icon={Shield} label="Club" value={p.club} />
        <Info icon={MapPin} label="Location" value={p.location} />
        <Info icon={Hand} label="Playing hand" value={HANDS[p.hand] || p.hand} />
        <Info icon={Users2} label="Preferred format" value={p.formats.map((f) => PREFS[f] || f).join(", ")} />
        <Info icon={Star} label="Skill level" value={p.skill} />
        <Info icon={Calendar} label="Joined" value={p.joined ? new Date(p.joined).toLocaleDateString() : ""} />
        <Info icon={ShieldCheck} label="Email" value={p.email_verified ? "Verified" : "Not verified"} />
        <Info icon={Facebook} label="Facebook" value={p.facebook_verified ? "Verified" : "Not verified"} />
      </div>
    </div>
  );
}

function RateCta({ el, playerId }) {
  const nav = useNavigate();
  if (!el) return null;
  const wait = el.checks.find((c) => c.key === "update_window" && !c.ok);
  return (
    <Card title="Rate this player" icon={PenLine} testid="rate-cta-card">
      {wait ? <div className="text-sm flex items-center gap-2" data-testid="already-rated"><Lock className="w-4 h-4 text-primary-c" />{wait.detail}</div>
        : el.eligible ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm">{el.existing ? <>You rated this player on {el.existing.updated_at.slice(0, 10)}. </> : null}Your rating counts as <b>{CAT[el.category].label}</b> ({CAT[el.category].pct}).</span>
            <button data-testid="rate-player-btn" onClick={() => nav(`/rate/${playerId}`)} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold">{el.existing ? "Update rating" : "Rate player"}</button>
          </div>
        ) : <EligibilityList el={el} />}
    </Card>
  );
}

function PrivateDetails({ d }) {
  const pr = d.profile || {}; const ec = pr.emergency_contact || {};
  return (
    <Card title="Organizer view · contact details" icon={ShieldCheck} testid="ppv-private">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Info icon={Mail} label="Email" value={d.email} />
        <Info icon={Phone} label="Phone" value={pr.phone} />
        <Info icon={Calendar} label="Date of birth" value={pr.date_of_birth} />
        <Info icon={Trophy} label="DUPR" value={pr.dupr_id} />
        <Info icon={ShieldCheck} label="Emergency contact" value={[ec.name, ec.relationship, ec.phone].filter(Boolean).join(" · ")} />
        <Info icon={Facebook} label="Facebook link" value={pr.facebook_url} />
      </div>
    </Card>
  );
}

function MatchList({ games }) {
  if (!games.length) return <Empty>No verified matches yet.</Empty>;
  return (
    <div className="divide-y" style={{ borderColor: "var(--border)" }}>
      {games.map((m, i) => (
        <div key={i} className="py-2.5 flex items-center gap-2 text-sm" data-testid="ppv-th-match">
          <WL win={m.win} />
          <span className="min-w-0 flex-1 truncate"><span style={{ color: "var(--text-2)" }}>vs</span> {m.opponents.join(" / ")} <span className="text-[10px] mono" style={{ color: "var(--text-2)" }}>{m.event} · {m.source === "club_war" ? "Club Wars" : "Tournament"}{m.category ? ` · ${m.category}` : ""}{m.round_label ? ` · ${m.round_label}` : ""}</span></span>
          {(m.points_for || m.points_against) ? <span className="mono text-xs shrink-0">{m.points_for}–{m.points_against} <span style={{ color: m.differential >= 0 ? "#22c55e" : "#ef4444" }}>{m.differential >= 0 ? "+" : ""}{m.differential}</span></span> : null}
          <span className="mono text-[11px] shrink-0" style={{ color: m.delta >= 0 ? "#22c55e" : "#ef4444" }}>{m.delta >= 0 ? "▲" : "▼"}{Math.abs(m.delta).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

function Feedback({ d, reload }) {
  const report = async (id) => {
    const reason = window.prompt("Why are you reporting this rating?");
    if (!reason) return;
    try { await api.post("/ratings/reports", { target_type: "rating", target_id: id, reason }); toast.success("Reported to administrators"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!d.feedback) return <Empty>Written feedback is private — only the player and administrators can read it. Aggregated scores are shown in Ratings.</Empty>;
  if (!d.feedback.length) return <Empty>No community feedback yet.</Empty>;
  return (
    <div className="divide-y" style={{ borderColor: "var(--border)" }}>
      {d.feedback.map((f) => (
        <div key={f.rating_id} className="py-3 text-sm" data-testid={`feedback-${f.rating_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] mono uppercase rounded-full glass px-2 py-0.5">{CAT[f.category].short}</span>
            <span className="mono font-bold">{Number(f.scores.overall).toFixed(2)}</span>
            {f.rater_name && <span className="text-xs" style={{ color: "var(--text-2)" }}>by {f.rater_name}</span>}
            {f.status !== "active" && <span className="text-[10px] mono uppercase" style={{ color: "#ef4444" }}>{f.status}</span>}
            <span className="ml-auto text-[11px] mono" style={{ color: "var(--text-2)" }}>{f.at.slice(0, 10)}</span>
            <button data-testid={`report-rating-${f.rating_id}`} onClick={() => report(f.rating_id)} className="text-[11px] flex items-center gap-1" style={{ color: "var(--text-2)" }}><Flag className="w-3 h-3" />Report</button>
          </div>
          {f.feedback && <p className="mt-1.5" style={{ color: "var(--text-2)" }}>“{f.feedback}”</p>}
        </div>
      ))}
    </div>
  );
}

export default function PlayerProfileView() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [priv, setPriv] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");
  const isOrg = user && (user.account_type === "organizer" || ["admin", "super_admin"].includes(user.role));

  const load = () => api.get(`/ratings/profile/${userId}`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); if (isOrg && userId !== "me") api.get(`/players/${userId}`).then(({ data }) => setPriv(data)).catch(() => {}); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const op = useMemo(() => (d ? localPlayerHistory(d.player.name) : null), [d]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-sm" style={{ background: "var(--bg)", color: "var(--text-2)" }}>{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  const s = d.stats;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Player Profile" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <Header p={d.player} isSelf={d.viewer.is_self} />
        <RatingHierarchy official={d.official} assessment={d.assessment} composite={d.composite} casual={d.casual} />
        <div className="glass rounded-full p-0.5 inline-flex flex-wrap gap-0.5" data-testid="profile-tabs">
          {TABS.map(([v, l]) => (
            <button key={v} data-testid={`profile-tab-${v}`} onClick={() => setTab(v)} className="rounded-full px-4 py-1.5 text-xs font-bold transition-colors"
              style={tab === v ? { background: "var(--primary)", color: "var(--on-primary)" } : { color: "var(--text-2)" }}>{l}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-5">
            {d.viewer.is_self && <CommunityRequestCard name={d.player.name} />}
            {d.viewer.is_self && <ShareRatingCard player={d.player} />}
            {d.viewer.is_self && <CasualSessionsCard />}
            {!d.viewer.is_self && <RateCta el={d.viewer.eligibility} playerId={d.player.user_id} />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Card title="Rating confidence" icon={ShieldCheck} testid="rating-confidence">
                <p className="text-sm">Official: <b>{d.official.rating == null ? "Unrated — plays its first verified match to get rated" : `${d.official.level} · ${d.official.matches} verified matches`}</b></p>
                <p className="text-sm mt-1">Assessment: <b>{d.assessment.total === 0 ? "No ratings yet" : d.assessment.provisional ? `${d.assessment.level} · missing some categories` : `${d.assessment.level} · all categories`}</b> · {d.assessment.total} ratings</p>
              </Card>
              <Card title="Recent matches" icon={Trophy}><MatchList games={d.match_history.slice(0, 5)} /></Card>
            </div>
            {priv && <PrivateDetails d={priv} />}
          </div>
        )}
        {tab === "ratings" && (
          <div className="space-y-5">
            <AssessmentBreakdown a={d.assessment} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Card title="Official rating trend" icon={Trophy}><TrendChart points={d.official.history} label="Official" testid="official-trend" /></Card>
              <Card title="Assessment rating history" icon={Users2}><TrendChart points={d.assessment_history} label="Assessment" testid="assessment-trend" /></Card>
            </div>
          </div>
        )}
        {tab === "matches" && (
          <div className="space-y-5">
            <Card title="Verified match history" icon={Trophy} testid="ppv-tournament-history"><MatchList games={d.match_history} /></Card>
            <Card title="Open Play" icon={Users2} testid="ppv-openplay-history" right={<span className="text-[10px] mono uppercase" style={{ color: "var(--text-2)" }}>this device · not rated</span>}>
              {!op || !op.games.length ? <Empty>No open-play games found on this device for this player.</Empty> : op.games.slice(0, 50).map((g, i) => (
                <div key={i} className="py-2 flex items-center gap-2 text-sm" data-testid="ppv-op-game">
                  {g.win != null && <WL win={g.win} />}
                  <span className="flex-1 truncate">{g.partners.length ? `w/ ${g.partners.join(", ")} ` : ""}vs {g.opponents.join(", ")}</span>
                  {g.points_for != null && <span className="mono text-xs">{g.points_for}–{g.points_against}</span>}
                </div>
              ))}
            </Card>
          </div>
        )}
        {tab === "events" && (
          <Card title="Event history" icon={Calendar} testid="ppv-events">
            {!d.events.length ? <Empty>No events yet.</Empty> : d.events.map((e) => (
              <div key={`${e.type}-${e.id}`} className="py-2.5 flex items-center gap-2 text-sm border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`ppv-event-${e.id}`}>
                <span className="font-semibold flex-1">{e.name}</span>
                <span className="text-[10px] mono uppercase glass rounded-full px-2 py-0.5">{e.type === "club_war" ? "Club Wars" : e.type === "external" ? `External${e.platform ? ` · ${e.platform}` : ""}` : "Tournament"}</span>
                <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{(e.date || "").slice(0, 10)}</span>
              </div>
            ))}
          </Card>
        )}
        {tab === "feedback" && <Card title="Community feedback" icon={Users2} testid="ppv-feedback"><Feedback d={d} reload={load} /></Card>}
        {tab === "stats" && (
          <Card title="Match statistics" icon={Trophy} testid="ppv-stats">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Stat label="Matches" value={s.matches} /><Stat label="Wins" value={s.wins} /><Stat label="Losses" value={s.losses} />
              <Stat label="Win rate" value={`${Math.round(s.win_rate * 100)}%`} /><Stat label="PPM" value={s.ppm} /><Stat label="Diff" value={`${s.differential >= 0 ? "+" : ""}${s.differential}`} />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {Object.entries(s.by_source).map(([k, v]) => <Stat key={k} label={k === "club_war" ? "Club Wars W-L" : "Tournament W-L"} value={`${v.wins}-${v.matches - v.wins}`} />)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
